<template>
  <ion-content class="modal-wrapper ion-padding">
    <ion-row>
      <ion-col size="12">
        <h4 class="h3 text-color-body text-align-center">
          {{ t('files.upload', { title: getDocumentSelected.name }) }}
        </h4>
      </ion-col>
      <ion-col size="12">
        <section class="message-wrapper mt-xs">
          <ion-icon :icon="informationCircleOutline" color="primary"></ion-icon>
          <ion-label class="text-color-placeholder text-xs"
            >{{ t('files.signContractManualDescription') }}
          </ion-label>
        </section>
      </ion-col>
    </ion-row>

    <ion-row v-if="getDocumentSelected" class="ba-grey pa-xs border-default document-card mt-8">
      <ion-col size="12" class="ion-no-padding d-flex justify-space-between align-center">
        <div class="modal-wrapper__pdf">
          <img src="@/assets/images/pdf-icon.png" alt="" />
        </div>
        <div class="d-flex w-100">
          <div class="pl-xs">
            <p class="font-sm text-start">{{ getDocumentSelected.name }}</p>
          </div>
        </div>
        <ion-button
          color="primary"
          shape="round"
          size="small"
          fill="clear"
          class="ion-no-padding"
          @click="openDownloadSign"
        >
          <ion-icon color="grey" :icon="cloudDownloadOutline"></ion-icon>
        </ion-button>
      </ion-col>
    </ion-row>
    <div>
      <UploadPhoto file-type="file" :label="'browseFile'" v-model="file" />
      <section v-if="file.size > 0" class="file-wrapper">
        <ion-row class="d-flex ion-align-items-center ion-justify-content-center">
          <ion-col size="3">
            <div class="modal-wrapper__pdf">
              <img src="@/assets/images/pdf-icon.png" alt="" />
            </div>
          </ion-col>
          <ion-col size="8">
            <h4 class="h4 text-color-placeholder text-weight-bold text-xs text-align-left">
              {{ file.title }}1
            </h4>
            <p class="text-color-placeholder text-xs text-align-left">{{ fileSize(file.size) }}1</p>
          </ion-col>
          <ion-col size="1">
            <ion-icon :icon="trash" @click="deleteFile" />
          </ion-col>
        </ion-row>
      </section>
    </div>
    <div class="d-flex justify-space-between mt-xs">
      <ion-button
        fill="clear"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="emit('close')"
      >
        {{ t('actions.cancel') }}
      </ion-button>
      <ion-button
        :disabled="loading || file.base64 === ''"
        fill="solid"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="uploadSignedContract"
      >
        {{ loading ? t('actions.uploading') : t('files.uploadDocument') }}
      </ion-button>
    </div>
  </ion-content>
</template>

<script setup lang="ts">
  import { IonButton, IonIcon, IonContent, IonRow, IonCol, IonLabel } from '@ionic/vue';
  import { cloudDownloadOutline, informationCircleOutline, trash } from 'ionicons/icons';
  import { onMounted, PropType, ref, watch } from 'vue';
  import { IDocument } from '@/interfaces/profile/profile-document.interface';
  import { useI18n } from 'vue-i18n';
  import UploadPhoto from '@/components/shared/UploadPhoto.vue';
  import { IUploadImage } from '@/interfaces/IUploadImage';
  import { fileSize } from '@/utils/global-utils';
  import { useProfile } from '@/composables/useProfile';
  import { useToast } from '@/composables/useToast';
  import { useProfileStore } from '@/stores/profile-store';
  import { IDocumentSignedManualForm } from '@/interfaces/profile/profile-response.interface';
  import { useBooking } from '@/composables/useBooking';
  import { storeToRefs } from 'pinia';
  import { useBookingStore } from '@/stores/booking-store';
  import { useAuth } from '@/composables/useAuth';
  import { SIGNATURE_TYPES } from '@/utils/constants/general-constants';

  const { documentSelected } = useProfile();
  const { user } = useAuth();
  const { handleDownloadContract, handleUploadSignedContract, handleGetReservationContract } =
    useBooking();
  const { toggleUploadManualContract } = storeToRefs(useBookingStore());
  const { getDocuments } = useProfileStore();
  const emit = defineEmits(['close']);

  const { showToast } = useToast();

  const { t } = useI18n();

  const inputSelected = ref();
  const loading = ref<boolean>(false);

  const props = defineProps({
    getDocumentSelected: {
      type: Object as PropType<IDocument>,
      required: true,
    },
    bookingId: {
      type: String,
      required: false,
    },
  });

  const toggleUploadPhoto = ref(false);
  const file = ref<IUploadImage>({
    base64: '',
    title: '',
    size: 0,
  });

  const deleteFile = () => {
    file.value = {
      base64: '',
      title: '',
      size: 0,
    };
  };

  const setNewDocumentFile = () => {
    if (props.getDocumentSelected?.type !== SIGNATURE_TYPES.MANUAL) {
      inputSelected.value.Answers.push({
        DocumentsMasterInputId: inputSelected.value.id,
        value: file.value.base64,
        size: file.value.size,
      });
      file.value.base64 = '';
      file.value.size = 0;
    }
  };

  const uploadSignedContract = async () => {
    try {
      if (file.value.base64 && user.value?.id && props.bookingId) {
        loading.value = true;

        const form: IDocumentSignedManualForm = {
          contractFile: file.value.base64 as string,
          tenantId: user.value?.id,
        };

        const response = await handleUploadSignedContract(props.bookingId, form);
        if (response?.success) {
          toggleUploadPhoto.value = false;
          deleteFile();
          toggleUploadManualContract.value = false;
          await handleGetReservationContract(props.bookingId);
          emit('close');
        } else {
          throw new Error(response?.error);
        }
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      await getDocuments();
      loading.value = false;
    }
  };

  const openDownloadSign = (item: IDocument) => {
    documentSelected.value = item;
    if (props.bookingId) {
      handleDownloadContract(props.bookingId).then((result) => {
        if (result?.data) {
          const blob = new Blob([result?.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `contract_${props.bookingId}.pdf`;
          link.click();
        }
      });
    }
  };

  const initialConfig = () => {
    createAnswerObject();
  };

  const createAnswerObject = () => {
    const dateInputs = props.getDocumentSelected?.Inputs.filter((input) => input.type === 'date');

    if (dateInputs) {
      dateInputs.forEach((input) => {
        if (input.Answers.length === 0) {
          input.Answers.push({
            DocumentsMasterInputId: input.id,
            value: '',
          });
        } else if (!input.Answers[0].id) {
          input.Answers = [];
          input.Answers.push({
            DocumentsMasterInputId: input.id,
            value: '',
          });
        }
      });
    }
  };

  onMounted(() => {
    initialConfig();
  });

  watch(file, (val) => {
    if (val.base64 !== '') {
      setNewDocumentFile();
    }
  });
</script>
<style scoped lang="scss">
  .document-wrapper {
    position: relative;

    &__status {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 2;
    }
  }

  ion-content.modal-wrapper {
    --border-radius: 25px;
    --background: white;
  }

  .file-wrapper {
    margin-top: 20px;
    margin-bottom: 30px;
    padding: 10px;
    border-radius: 5px;
    border: 1px solid #d4ddff;
  }

  .show-file-wrapper {
    position: relative;

    img {
      object-fit: cover;
      height: 170px;
      width: 100%;
    }

    &__menu {
      position: absolute;
      top: 10px;
      right: 10px;
    }
  }

  .upload-wrapper {
    position: relative;
    height: 170px;
    background: #d4ddff66;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &__icon {
      display: flex;
      justify-items: center;
      align-items: center;

      img {
        width: 30px;
        height: 30px;
        margin: 0 auto;
      }
    }

    &__menu {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    &__preview {
      height: 170px;
      width: 100%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      &__icon {
        width: 100%;
        display: block;
      }

      img {
        width: 100%;
        height: 190px;
        object-fit: cover;
      }

      h1 {
        font-size: 30px;
        font-weight: bold;
        color: white;
      }
    }
  }

  .document-card {
    img {
      width: 44px;
      object-fit: cover;
      height: 44px;
    }
  }

  .modal-wrapper {
    &__pdf {
      img {
        width: 45px;
        height: 40px;
        border-radius: 10px;
      }
    }
  }
</style>
